const OCR_METADATA_KEYS = new Set([
  'confidence_score',
  'processed_at',
  'ocr_confidence_score',
  'ocr_processed_at',
  'hr_edited_fields',
  'validation_warnings',
]);

const hasMeaningfulValue = (value) => {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim() !== '';
  if (Array.isArray(value)) return value.some(hasMeaningfulValue);
  if (typeof value === 'object') {
    return Object.values(value).some(hasMeaningfulValue);
  }
  return true;
};

/**
 * Determine whether an OCR payload contains data that the verification form can use.
 *
 * Backend endpoints intentionally expose different field naming conventions:
 * some use `ocr_*` keys while family and qualification documents use form-facing
 * keys. Checking prefixes therefore discards valid persisted data.
 *
 * @param {Record<string, unknown>} data OCR payload after document-specific normalization.
 * @param {string[]} fieldKeys Keys rendered by the current verification form.
 * @returns {boolean} Whether at least one form value is populated.
 */
export const hasMeaningfulDocumentOcrData = (data, fieldKeys = []) => {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return false;

  const keysToCheck = fieldKeys.length > 0
    ? fieldKeys
    : Object.keys(data).filter((key) => !OCR_METADATA_KEYS.has(key));

  return keysToCheck.some((key) => hasMeaningfulValue(data[key]));
};

const QUALIFICATION_PAGE_ARRAY_KEYS = ['professional_certs', 'education'];

const qualificationPageHasExtract = (page) => {
  if (!page || typeof page !== 'object' || Array.isArray(page)) return false;
  if (page.ocr_data || page.extracted_data || page.qualification_data) return true;
  for (const key of QUALIFICATION_PAGE_ARRAY_KEYS) {
    const items = page[key];
    if (
      Array.isArray(items)
      && items.some((item) => item && typeof item === 'object' && Object.keys(item).length > 0)
    ) {
      return true;
    }
  }
  return Object.keys(page).some((key) => key.startsWith('ocr_') && hasMeaningfulValue(page[key]));
};

const unwrapQualificationPage = (page) => {
  if (!page || typeof page !== 'object' || Array.isArray(page)) return page || {};
  if (page.data && typeof page.data === 'object' && !Array.isArray(page.data)) {
    return page.data;
  }
  return page;
};

/**
 * n8n runs one LLM pass per PDF page and returns a list of
 * `{ education, professional_certs }` objects. Page 1 is often empty
 * (card back / T&Cs). Pick the first page that actually extracted a cert.
 *
 * @param {unknown} n8nResponse Raw n8n body: list of pages, a single page, or `{ data: ... }`.
 * @returns {Record<string, unknown>} The first page with extractable qualification fields.
 */
export const firstMeaningfulQualificationN8nPage = (n8nResponse) => {
  const root = (n8nResponse && typeof n8nResponse === 'object' && !Array.isArray(n8nResponse)
    && Array.isArray(n8nResponse.data))
    ? n8nResponse.data
    : n8nResponse;
  const pages = Array.isArray(root) ? root : [root];
  for (const page of pages) {
    const inner = unwrapQualificationPage(page);
    if (qualificationPageHasExtract(inner)) return inner;
  }
  const first = pages.find((page) => page && typeof page === 'object') || {};
  return unwrapQualificationPage(first);
};
